import React, { useState, useEffect } from 'react';
import './PropertyDetail.css';
import { supabase } from '../supabaseClient';
import ExpenseSummary from './ExpenseSummary';
import MonthNavigator from '../components/MonthNavigator';
import MonthlyHistoryCard, { earliestMonth } from '../components/MonthlyHistoryCard';
import { InvestmentForm } from '../components/InvestmentForm';
import { AddExpenseModal, ExpenseMenu, PendingVariableInput, DeleteExpenseModal, DuplicateExpenseModal, calcPaymentsMade } from './PropertyDetail';

function isFutureMonth(year, month) {
  const now = new Date();
  return year > now.getFullYear() || (year === now.getFullYear() && month > now.getMonth());
}

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay(); // 0=domingo
}

function getExpensesForMonth(expenses, year, month) {
  return expenses.filter(e => {
    const isUnico = e.type === 'puntual' || e.type === 'unico' || e.frequency === 'unico';
    if (isUnico) {
      if (!e.start_date) return false;
      const d = new Date(e.start_date.substring(0, 10) + 'T12:00:00');
      return d.getFullYear() === year && d.getMonth() === month;
    }
    const dateStr = e.start_date
      ? (e.start_date.substring(0, 10) + 'T12:00:00')
      : (e.created_at ? (e.created_at.substring(0, 10) + 'T12:00:00') : '');
    const start = new Date(dateStr);
    if (isNaN(start.getTime())) return false;
    const sy = start.getFullYear(), sm = start.getMonth();
    if (year < sy || (year === sy && month < sm)) return false;
    if (e.frequency === 'manual') return true;
    const monthsDiff = (year - sy) * 12 + (month - sm);
    const step = getFrequencyStep(e);
    if (e.type === 'recurrente_temporal') {
      if (monthsDiff >= (e.duration_payments || 0) * step) return false;
    }
    if (e.skipped_months && Array.isArray(e.skipped_months)) {
      const monthStr = `${year}-${String(month + 1).padStart(2, '0')}`;
      if (e.skipped_months.includes(monthStr)) return false;
    }
    return true;
  });
}

function getMonthlyEquivalent(expense) {
  const amt = Number(expense.amount) || 0;
  if (expense.frequency === 'trimestral') return amt / 3;
  if (expense.frequency === 'anual') return amt / 12;
  if (expense.frequency === 'custom') return amt / (expense.custom_frequency_months || 1);
  return amt;
}

function getExpensesForMonthReal(expenses, year, month) {
  return expenses.filter(e => {
    const isUnico = e.type === 'puntual' || e.type === 'unico' || e.frequency === 'unico';
    if (isUnico) {
      if (!e.start_date) return false;
      const d = new Date(e.start_date.substring(0, 10) + 'T12:00:00');
      return d.getFullYear() === year && d.getMonth() === month;
    }
    const dateStr = e.start_date
      ? (e.start_date.substring(0, 10) + 'T12:00:00')
      : (e.created_at ? (e.created_at.substring(0, 10) + 'T12:00:00') : '');
    const start = new Date(dateStr);
    if (isNaN(start.getTime())) return false;
    const sy = start.getFullYear(), sm = start.getMonth();
    if (year < sy || (year === sy && month < sm)) return false;
    if (e.frequency === 'manual') return true;
    const monthsDiff = (year - sy) * 12 + (month - sm);
    const step = getFrequencyStep(e);
    if (monthsDiff % step !== 0) return false;
    if (e.type === 'recurrente_temporal') {
      const paymentIndex = monthsDiff / step;
      if (paymentIndex >= (e.duration_payments || 0)) return false;
    }
    if (e.skipped_months && Array.isArray(e.skipped_months)) {
      const monthStr = `${year}-${String(month + 1).padStart(2, '0')}`;
      if (e.skipped_months.includes(monthStr)) return false;
    }
    return true;
  });
}

function getAmountForView(expense, view) {
  if (view === 'real') return Number(expense.amount) || 0;
  return getMonthlyEquivalent(expense);
}

function getFrequencyStep(expense) {
  if (expense.frequency === 'trimestral') return 3;
  if (expense.frequency === 'anual') return 12;
  if (expense.frequency === 'custom') return expense.custom_frequency_months || 1;
  return 1;
}

function getCurrentPeriodStart(expense, year, month) {
  if (!expense.start_date) return null;
  const start = new Date(expense.start_date.substring(0, 10) + 'T12:00:00');
  const sy = start.getFullYear(), sm = start.getMonth();
  const step = getFrequencyStep(expense);
  const monthsDiff = (year - sy) * 12 + (month - sm);
  const periodIndex = Math.floor(monthsDiff / step);
  const totalStartMonths = sy * 12 + sm + periodIndex * step;
  const pYear = Math.floor(totalStartMonths / 12);
  const pMonth = totalStartMonths % 12;
  return `${pYear}-${String(pMonth + 1).padStart(2, '0')}-01`;
}

function isExpensePendingVariable(expense, year, month) {
  if (expense.type !== 'recurrente_variable') return false;
  if (expense.frequency === 'manual') return false;
  const currentPeriodStart = getCurrentPeriodStart(expense, year, month);
  if (!currentPeriodStart) return false;
  const savedPeriodStart = expense.period_start
    ? expense.period_start.substring(0, 10)
    : null;
  return savedPeriodStart !== currentPeriodStart;
}

function isPaymentMonth(expense, year, month) {
  if (!expense.start_date) return true;
  const start = new Date(expense.start_date.substring(0, 10) + 'T12:00:00');
  const sy = start.getFullYear(), sm = start.getMonth();
  const step = getFrequencyStep(expense);
  const monthsDiff = (year - sy) * 12 + (month - sm);
  return monthsDiff % step === 0;
}

const DAY_MS = 1000 * 60 * 60 * 24;

// Local-noon Date from a 'YYYY-MM-DD' booking date (avoids DST/UTC drift)
function parseBookingDay(str) {
  if (!str) return null;
  const d = new Date(String(str).substring(0, 10) + 'T12:00:00');
  return isNaN(d.getTime()) ? null : d;
}

// Nights of a booking that fall inside (year, month). A night belongs to the day
// it starts: check-in 30/01, check-out 02/02 → 2 nights in January, 1 in February.
function nightsInMonth(booking, year, month) {
  const start = parseBookingDay(booking.startDate);
  const end = parseBookingDay(booking.endDate);
  if (!start || !end || end <= start) return [];
  const monthStart = new Date(year, month, 1, 12);
  const monthEnd = new Date(year, month + 1, 1, 12); // exclusive
  const from = start > monthStart ? start : monthStart;
  const to = end < monthEnd ? end : monthEnd;
  const days = [];
  for (let d = new Date(from); d < to; d = new Date(d.getTime() + DAY_MS)) {
    days.push(d.getDate());
  }
  return days;
}

function totalNights(booking) {
  const start = parseBookingDay(booking.startDate);
  const end = parseBookingDay(booking.endDate);
  if (!start || !end || end <= start) return 0;
  return Math.round((end - start) / DAY_MS);
}

// ─────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────
function VacationalDetail({ property, onBack, onUpdate, landlordEmail, readOnly = false }) { // eslint-disable-line no-unused-vars
  const now = new Date();
  const [currentYear, setCurrentYear] = useState(now.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(now.getMonth());
  const [expenses, setExpenses] = useState([]);
  const [bookings, setBookings] = useState(property.bookings || []);
  const [showInvestmentSection, setShowInvestmentSection] = useState(false);

  React.useEffect(() => { setBookings(property.bookings || []); }, [property.bookings]);

  useEffect(() => {
    supabase
      .from('expenses')
      .select('*')
      .eq('property_id', String(property.id))
      .order('created_at', { ascending: false })
      .then(({ data }) => { if (data) setExpenses(data); });
  }, [property.id]);

  const [showAddBooking, setShowAddBooking] = useState(false);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [showExpenseSummary, setShowExpenseSummary] = useState(false);
  const [showExpenses, setShowExpenses] = useState(false);
  const [expenseView, setExpenseView] = useState(() => {
    try { return localStorage.getItem(`expenseView_${property.id}`) || 'prorrateado'; } catch { return 'prorrateado'; }
  }); // 'prorrateado' | 'real'
  const [editingExpense, setEditingExpense] = useState(null);
  const [openMenuExpenseId, setOpenMenuExpenseId] = useState(null);
  const [deleteExpenseModal, setDeleteExpenseModal] = useState(null); // { expense }
  const [duplicateExpenseModal, setDuplicateExpenseModal] = useState(null); // { newExpenseData, conflict, prevStartDates, canExtend }
  const [attachmentLightbox, setAttachmentLightbox] = useState(null);
  const [editingBooking, setEditingBooking] = useState(null);
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const optionsMenuRef = React.useRef(null);
  React.useEffect(() => {
    if (!showOptionsMenu) return;
    const handleOutside = (e) => {
      if (!optionsMenuRef.current?.contains(e.target)) {
        setShowOptionsMenu(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [showOptionsMenu]);
  const [showEditProperty, setShowEditProperty] = useState(false);

  const minYear = 2020;
  const minMonth = 0;

  const isAtMinMonth = currentYear === minYear && currentMonth === minMonth;
  const isAtCurrentMonth = currentYear === now.getFullYear() && currentMonth === now.getMonth();
  const isCurrentMonthFuture = isFutureMonth(currentYear, currentMonth);
  const isPastMonth = !isAtCurrentMonth && !isCurrentMonthFuture;
  const [isEditMode, setIsEditMode] = useState(false);
  const canEdit = !readOnly && (isAtCurrentMonth || isEditMode);
  const ownershipMultiplier = (property.ownershipPercentage || 100) / 100;

  const goToPrevMonth = () => {
    if (isAtMinMonth) return;
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(y => y - 1); }
    else setCurrentMonth(m => m - 1);
  };
  const goToNextMonth = () => {
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(y => y + 1); }
    else setCurrentMonth(m => m + 1);
  };

  useEffect(() => {
    setIsEditMode(false);
  }, [currentYear, currentMonth]);

  // Reservas del mes actual
  const monthBookings = bookings.filter(b => {
    const start = new Date(b.startDate);
    const end = new Date(b.endDate);
    const monthStart = new Date(currentYear, currentMonth, 1);
    const monthEnd = new Date(currentYear, currentMonth + 1, 0);
    return start <= monthEnd && end >= monthStart;
  });

  const monthIncome = monthBookings
    .filter(b => b.status === 'confirmed')
    .reduce((sum, b) => sum + (b.amount || 0), 0) * ownershipMultiplier;

  const visibleExpenses = (expenseView === 'real'
    ? getExpensesForMonthReal(expenses, currentYear, currentMonth)
    : getExpensesForMonth(expenses, currentYear, currentMonth)
  ).filter(e => e.active !== false);
  const ownershipPct = property.ownershipPercentage || 100;
  const totalExpenses = visibleExpenses.reduce((sum, e) => {
    if (e.active === false) return sum;
    if (expenseView !== 'real' && isExpensePendingVariable(e, currentYear, currentMonth)) return sum;
    return sum + getAmountForView(e, expenseView);
  }, 0);
  const myExpenses = visibleExpenses.reduce((sum, e) => {
    if (e.active === false) return sum;
    if (expenseView !== 'real' && isExpensePendingVariable(e, currentYear, currentMonth)) return sum;
    const pct = e.expense_percentage != null ? e.expense_percentage : ownershipPct;
    return sum + getAmountForView(e, expenseView) * pct / 100;
  }, 0);

  const netIncome = isCurrentMonthFuture ? null : monthIncome - myExpenses;

  // Gauge fill
  const maxRef = property.price || 1000;
  const gaugeFill = netIncome !== null && netIncome > 0 ? Math.min(netIncome / maxRef, 1) : 0;

  const handleAddBooking = (bookingData) => {
    const updated = editingBooking
      ? bookings.map(b => b.id === editingBooking.id ? { ...bookingData, id: b.id } : b)
      : [...bookings, { ...bookingData, id: Date.now().toString() }];
    setBookings(updated);
    onUpdate({ ...property, bookings: updated });
    setShowAddBooking(false);
    setEditingBooking(null);
  };

  const handleDeleteBooking = (id) => {
    if (window.confirm('¿Eliminar esta reserva?')) {
      const updated = bookings.filter(b => b.id !== id);
      setBookings(updated);
      onUpdate({ ...property, bookings: updated });
    }
  };

  // ─── Gastos: same behaviour as PropertyDetail ───
  const doInsertExpense = async (expenseData, prevStartDates) => {
    const base = { property_id: String(property.id), landlord_email: landlordEmail, ...expenseData };
    const rows = (prevStartDates && prevStartDates.length > 0)
      ? [base, ...prevStartDates.map(sd => ({ ...base, start_date: sd }))]
      : [base];
    const { data, error } = await supabase.from('expenses').insert(rows).select();
    if (error) { alert(`Error guardando el gasto: ${error.message}`); return; }
    setExpenses(prev => [...(data || []), ...prev]);
    setShowAddExpense(false);
  };

  const handleAddExpense = async (expenseData, prevStartDates) => {
    // Duplicate recurring expense: same category + frequency, both active in the viewed month
    if (expenseData.frequency !== 'unico') {
      const newExpenseActive = getExpensesForMonth([{ ...expenseData, active: true }], currentYear, currentMonth).length > 0;
      if (newExpenseActive) {
        const conflict = expenses.find(e => {
          if (e.active === false) return false;
          if (e.frequency === 'unico' || e.type === 'puntual') return false;
          if (e.category !== expenseData.category) return false;
          if (e.frequency !== expenseData.frequency) return false;
          return getExpensesForMonth([e], currentYear, currentMonth).length > 0;
        });
        if (conflict) {
          const newStart = new Date(expenseData.start_date.substring(0, 10) + 'T12:00:00');
          const existStart = new Date((conflict.start_date || conflict.created_at || '').substring(0, 10) + 'T12:00:00');
          setDuplicateExpenseModal({ newExpenseData: expenseData, conflict, prevStartDates, canExtend: existStart > newStart });
          return;
        }
      }
    }
    await doInsertExpense(expenseData, prevStartDates);
  };

  const handleExtendExistingExpense = async () => {
    const { newExpenseData, conflict } = duplicateExpenseModal;
    const { error } = await supabase
      .from('expenses')
      .update({ start_date: newExpenseData.start_date })
      .eq('id', conflict.id);
    if (error) { alert(`Error actualizando el gasto: ${error.message}`); return; }
    setExpenses(prev => prev.map(e => e.id === conflict.id ? { ...e, start_date: newExpenseData.start_date } : e));
    setDuplicateExpenseModal(null);
    setShowAddExpense(false);
  };

  const handleDeleteExpense = async (expense) => {
    const isRecurring = expense.type === 'recurrente_fijo' || expense.type === 'recurrente_temporal';
    if (isRecurring) {
      setDeleteExpenseModal({ expense });
    } else {
      const { error } = await supabase.from('expenses').delete().eq('id', expense.id);
      if (error) { alert(`Error al eliminar el gasto: ${error.message}`); return; }
      setExpenses(prev => prev.filter(e => e.id !== expense.id));
    }
  };

  const handleSkipExpenseMonth = async (expenseId) => {
    const expense = expenses.find(e => e.id === expenseId);
    const step = getFrequencyStep(expense || {});
    const monthsToSkip = [];
    let y = currentYear, m = currentMonth;
    for (let i = 0; i < step; i++) {
      monthsToSkip.push(`${y}-${String(m + 1).padStart(2, '0')}`);
      if (m === 11) { m = 0; y++; } else { m++; }
    }
    const updatedSkipped = [...new Set([...(expense?.skipped_months || []), ...monthsToSkip])];
    const { error } = await supabase.from('expenses').update({ skipped_months: updatedSkipped }).eq('id', expenseId);
    if (error) { alert(`Error actualizando el gasto: ${error.message}`); return; }
    setExpenses(prev => prev.map(e => e.id === expenseId ? { ...e, skipped_months: updatedSkipped } : e));
    setDeleteExpenseModal(null);
  };

  const handleDeleteExpensePermanently = async (expenseId) => {
    const { error } = await supabase.from('expenses').delete().eq('id', expenseId);
    if (error) { alert(`Error al eliminar el gasto: ${error.message}`); return; }
    setExpenses(prev => prev.filter(e => e.id !== expenseId));
    setDeleteExpenseModal(null);
  };

  const handleUpdateExpense = async (expenseId, expenseData) => {
    const { data, error } = await supabase
      .from('expenses')
      .update(expenseData)
      .eq('id', expenseId)
      .select()
      .single();
    if (error) { alert(`Error actualizando el gasto: ${error.message}`); return; }
    setExpenses(prev => prev.map(e => e.id === expenseId ? data : e));
    setEditingExpense(null);
  };

  const handleUpdateVariableAmount = async (id, newAmount) => {
    const expense = expenses.find(e => e.id === id);
    const periodStart = getCurrentPeriodStart(expense, currentYear, currentMonth);
    const { error } = await supabase.from('expenses').update({
      amount: parseFloat(newAmount),
      period_start: periodStart,
    }).eq('id', id);
    if (error) { alert(`Error actualizando el gasto: ${error.message}`); return; }
    setExpenses(prev => prev.map(e =>
      e.id === id
        ? { ...e, amount: parseFloat(newAmount), period_start: periodStart }
        : e
    ));
  };

  const refreshExpenses = () => {
    supabase
      .from('expenses')
      .select('*')
      .eq('property_id', String(property.id))
      .order('created_at', { ascending: false })
      .then(({ data }) => { if (data) setExpenses(data); });
  };

  // ─── Historial ───
  const historyStart = earliestMonth([
    property.createdAt,
    ...bookings.map(b => parseBookingDay(b.startDate)),
    ...expenses.map(e => e.start_date ? e.start_date.substring(0, 10) + 'T12:00:00' : null),
  ]);

  // Income: confirmed bookings, split across months by nights.
  // Status: booked nights / nights in the month (overlapping bookings count once).
  const computeHistoryMonth = (y, m) => {
    let income = 0;
    const bookedDays = new Set();
    bookings.forEach(b => {
      if (b.status !== 'confirmed') return;
      const days = nightsInMonth(b, y, m);
      if (days.length === 0) return;
      const nights = totalNights(b);
      income += nights > 0 ? (Number(b.amount) || 0) * days.length / nights : 0;
      days.forEach(d => bookedDays.add(d));
    });
    income *= ownershipMultiplier;

    const monthExp = expenseView === 'real'
      ? getExpensesForMonthReal(expenses, y, m)
      : getExpensesForMonth(expenses, y, m);
    const exp = monthExp.reduce((sum, e) => {
      if (e.active === false) return sum;
      if (expenseView !== 'real' && isExpensePendingVariable(e, y, m)) return sum;
      const pct = e.expense_percentage != null ? e.expense_percentage : (property.ownershipPercentage || 100);
      return sum + getAmountForView(e, expenseView) * pct / 100;
    }, 0);

    const booked = bookedDays.size;
    const monthDays = getDaysInMonth(y, m);
    const status = booked > 0
      ? { label: `${booked}/${monthDays} · ${Math.round(booked / monthDays * 100)}%`, active: true }
      : { label: 'Sin reservas', active: false };
    return { income, expenses: exp, status };
  };

  // Calendario
  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDay = (getFirstDayOfMonth(currentYear, currentMonth) + 6) % 7; // lunes=0

  const getBookingForDay = (day) => {
    return bookings.filter(b => {
      const start = new Date(b.startDate);
      const end = new Date(b.endDate);
      const d = new Date(currentYear, currentMonth, day);
      return d >= new Date(start.getFullYear(), start.getMonth(), start.getDate()) &&
             d <= new Date(end.getFullYear(), end.getMonth(), end.getDate());
    });
  };

  const platformColor = (platform) => {
    const map = { airbnb: '#FF5A5F', booking: '#003580', directo: '#4CAF50', otro: '#9C27B0' };
    return map[platform] || '#666';
  };

  // ─── Datos de inversión: ingresos de reservas para InvestmentForm ───
  const vacOwnership = (property.ownershipPercentage || 100) / 100;
  const vacBookingIncome = (year, month) => bookings
    .filter(b => {
      const s = new Date(b.startDate);
      return b.status === 'confirmed' && s.getFullYear() === year && s.getMonth() === month;
    })
    .reduce((s, b) => s + (Number(b.amount) || 0), 0);
  const vacLastMonths = (n) => {
    const out = [];
    let y = now.getFullYear(), m = now.getMonth();
    for (let i = 0; i < n; i++) {
      out.push({ year: y, month: m });
      if (m === 0) { m = 11; y--; } else { m--; }
    }
    return out;
  };
  // Expected monthly income: reference price if set, else last 12 months of bookings / 12 (captures seasonality)
  const vacLast12Total = vacLastMonths(12).reduce((s, { year, month }) => s + vacBookingIncome(year, month), 0);
  const vacMonthlyIncome = (Number(property.price) || 0) > 0
    ? Number(property.price) * vacOwnership
    : (vacLast12Total / 12) * vacOwnership;
  // Recent average: months with bookings in the last 6 months (null if none)
  const vacRecent = vacLastMonths(6).map(({ year, month }) => vacBookingIncome(year, month)).filter(v => v > 0);
  const vacAvgRecentIncome = vacRecent.length > 0
    ? (vacRecent.reduce((s, v) => s + v, 0) / vacRecent.length) * vacOwnership
    : null;

  if (showExpenseSummary) {
    // Same income as the Historial card: confirmed bookings split by nights
    const getMonthlyIncome = (yr, mo) => computeHistoryMonth(yr, mo).income;
    return (
      <ExpenseSummary
        expenses={expenses}
        onBack={() => setShowExpenseSummary(false)}
        propertyName={property.name}
        getMonthlyIncome={getMonthlyIncome}
      />
    );
  }

  return (
    <div className="property-detail-container">
      {/* Header */}
      <div className="detail-header" ref={optionsMenuRef}>
        <button className="back-button" onClick={onBack}>←</button>
        <h1 className="detail-title">{property.name}</h1>
        <button className="detail-options" onClick={() => setShowOptionsMenu(!showOptionsMenu)}>⋮</button>
        {showOptionsMenu && (
          <div className="detail-options-menu">
            <button className="detail-option-item" onClick={() => { setShowEditProperty(true); setShowOptionsMenu(false); }}>Editar propiedad</button>
            <button className="detail-option-item delete" onClick={() => {
              if (window.confirm('¿Eliminar esta propiedad?')) { onUpdate({ ...property, deleted: true }); onBack(); }
              setShowOptionsMenu(false);
            }}>Eliminar propiedad</button>
          </div>
        )}
      </div>

      {/* Month navigator */}
      <MonthNavigator
        year={currentYear}
        month={currentMonth}
        minYear={minYear}
        minMonth={minMonth}
        onPrev={goToPrevMonth}
        onNext={goToNextMonth}
        onJump={(yr, mo) => { setCurrentYear(yr); setCurrentMonth(mo); }}
        isPastMonth={isPastMonth}
        isEditMode={isEditMode}
        onEdit={readOnly ? undefined : () => setIsEditMode(true)}
        onSave={() => setIsEditMode(false)}
        onCancel={() => setIsEditMode(false)}
      />

      {/* Gauge */}
      <div className="profitability-section">
        <div className="gauge-chart">
          <svg width="360" height="220" viewBox="0 0 360 220">
            <path d="M 50 180 A 130 130 0 0 1 310 180" fill="none" stroke="#E0E0E0" strokeWidth="35" strokeLinecap="round"/>
            {!isCurrentMonthFuture && netIncome !== null && netIncome >= 0 && (
              <path d="M 50 180 A 130 130 0 0 1 310 180" fill="none" stroke="#4CAF50" strokeWidth="35" strokeLinecap="round"
                strokeDasharray="408" strokeDashoffset={408 - gaugeFill * 408}/>
            )}
            {!isCurrentMonthFuture && netIncome !== null && netIncome < 0 && (
              <path d="M 50 180 A 130 130 0 0 1 310 180" fill="none" stroke="#F44336" strokeWidth="35" strokeLinecap="round"/>
            )}
          </svg>
          <div className="gauge-center">
            {isCurrentMonthFuture ? (
              <><div className="net-income future">—</div><div className="income-sublabel">Mes futuro</div></>
            ) : (
              <>
                <div className="net-income">{netIncome >= 0 ? '+' : ''}{netIncome.toFixed(2)} €</div>
                <div className="income-label">/mes</div>
                <div className="income-sublabel">Neto</div>
              </>
            )}
          </div>
        </div>
        <p className="profitability-label">{isCurrentMonthFuture ? 'Sin datos todavía' : 'Rentabilidad mensual'}</p>
      </div>

      {/* Calendario */}
      <div className="info-card">
        <div className="card-header" style={{ marginBottom: '14px' }}>
          <h3>Calendario</h3>
          {canEdit && (
            <button className="payment-btn confirm small" style={{ flex: 'none', padding: '8px 14px' }}
              onClick={() => { setEditingBooking(null); setShowAddBooking(true); }}>
              + Reserva
            </button>
          )}
        </div>

        {/* Días semana */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', marginBottom: '4px' }}>
          {['L','M','X','J','V','S','D'].map(d => (
            <div key={d} style={{ textAlign: 'center', fontSize: '11px', fontWeight: 600, color: '#aaa', padding: '4px 0' }}>{d}</div>
          ))}
        </div>

        {/* Días del mes */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
          {Array.from({ length: firstDay }).map((_, i) => <div key={`e${i}`} />)}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const dayBookings = getBookingForDay(day);
            const isToday = currentYear === now.getFullYear() && currentMonth === now.getMonth() && day === now.getDate();
            const booking = dayBookings[0];
            return (
              <div key={day} style={{
                textAlign: 'center', padding: '6px 2px', borderRadius: '8px', fontSize: '13px',
                background: booking ? platformColor(booking.platform) + '22' : 'transparent',
                border: isToday ? '2px solid #2196F3' : '2px solid transparent',
                position: 'relative', cursor: booking && canEdit ? 'pointer' : 'default',
                fontWeight: isToday ? 700 : 400,
                color: booking ? platformColor(booking.platform) : '#333',
              }}
                onClick={() => { if (booking && canEdit) { setEditingBooking(booking); setShowAddBooking(true); } }}
              >
                {day}
                {booking && (
                  <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: platformColor(booking.platform), margin: '2px auto 0' }} />
                )}
              </div>
            );
          })}
        </div>

        {/* Leyenda plataformas */}
        <div style={{ display: 'flex', gap: '12px', marginTop: '14px', flexWrap: 'wrap' }}>
          {[['airbnb','#FF5A5F','Airbnb'], ['booking','#003580','Booking'], ['directo','#4CAF50','Directo'], ['otro','#9C27B0','Otro']].map(([key, color, label]) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: '#666' }}>
              <div style={{ width: '10px', height: '10px', borderRadius: '3px', background: color }} />
              {label}
            </div>
          ))}
        </div>
      </div>

      {/* Reservas del mes */}
      <div className="info-card">
        <div className="card-header" style={{ marginBottom: monthBookings.length > 0 ? '14px' : '0' }}>
          <h3>Reservas de {new Date(currentYear, currentMonth).toLocaleDateString('es-ES', { month: 'long' })}</h3>
          <span style={{ fontSize: '13px', color: '#4CAF50', fontWeight: 600 }}>
            {monthBookings.filter(b => b.status === 'confirmed').reduce((s, b) => s + (b.amount || 0), 0).toFixed(0)} €
          </span>
        </div>

        {monthBookings.length === 0 ? (
          <p style={{ fontSize: '13px', color: '#aaa', textAlign: 'center', padding: '12px 0', margin: 0 }}>
            No hay reservas este mes
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {monthBookings.map(booking => {
              const start = new Date(booking.startDate);
              const end = new Date(booking.endDate);
              const nights = Math.round((end - start) / (1000 * 60 * 60 * 24));
              const color = platformColor(booking.platform);
              return (
                <div key={booking.id} style={{
                  background: '#f9f9f9', borderRadius: '12px', padding: '12px 14px',
                  borderLeft: `3px solid ${color}`, cursor: 'pointer'
                }} onClick={() => { if (!canEdit) return; setEditingBooking(booking); setShowAddBooking(true); }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <p style={{ margin: 0, fontWeight: 600, fontSize: '14px', color: '#111' }}>{booking.guestName}</p>
                      <p style={{ margin: '3px 0 0', fontSize: '12px', color: '#888' }}>
                        {start.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })} — {end.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })} · {nights} noche{nights !== 1 ? 's' : ''}
                      </p>
                      {booking.guestPhone && (
                        <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#aaa' }}>{booking.guestPhone}</p>
                      )}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ margin: 0, fontWeight: 700, fontSize: '15px', color: '#111' }}>{booking.amount} €</p>
                      <span style={{
                        fontSize: '11px', padding: '2px 8px', borderRadius: '10px', display: 'inline-block', marginTop: '3px',
                        background: booking.status === 'confirmed' ? '#E8F5E9' : booking.status === 'cancelled' ? '#FFEBEE' : '#FFF3E0',
                        color: booking.status === 'confirmed' ? '#388E3C' : booking.status === 'cancelled' ? '#C62828' : '#E65100',
                      }}>
                        {booking.status === 'confirmed' ? 'Confirmada' : booking.status === 'cancelled' ? 'Cancelada' : 'Pendiente'}
                      </span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px' }}>
                    <div style={{ width: '8px', height: '8px', borderRadius: '2px', background: color }} />
                    <span style={{ fontSize: '12px', color: '#888', textTransform: 'capitalize' }}>{booking.platform}</span>
                    {booking.notes && <span style={{ fontSize: '12px', color: '#aaa', marginLeft: '4px' }}>· {booking.notes}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Gastos */}
      <div className="info-card expenses-card">
        <div className="card-header clickable" onClick={() => setShowExpenses(!showExpenses)} style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0 }}>Gastos</h3>
            <span className="expenses-total" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>{totalExpenses.toFixed(2)} {expenseView === 'real' ? '€' : '€/mes'}</span>
              <span className="arrow">{showExpenses ? '▼' : '›'}</span>
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }} onClick={e => e.stopPropagation()}>
            <button type="button" onClick={() => setShowExpenseSummary(true)}
              style={{ fontSize: 11, padding: '3px 9px', borderRadius: 8, border: '1px solid #ddd', background: 'white', color: '#666', cursor: 'pointer' }}>
              Resumen
            </button>
            <div style={{ display: 'flex', background: '#f0f0f0', borderRadius: 6, padding: 2, gap: 1 }}>
              {['prorrateado', 'real'].map(v => (
                <button key={v} onClick={() => { try { localStorage.setItem(`expenseView_${property.id}`, v); } catch { /* ignore */ } setExpenseView(v); }} style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, border: 'none', background: expenseView === v ? 'white' : 'transparent', color: expenseView === v ? '#333' : '#999', cursor: 'pointer', fontWeight: expenseView === v ? 600 : 400 }}>
                  {v === 'prorrateado' ? 'Prorrat.' : 'Real'}
                </button>
              ))}
            </div>
          </div>
        </div>
        {showExpenses && (
          <div className="expenses-detail">
            {visibleExpenses.length === 0 ? (
              <p className="no-expenses">{isCurrentMonthFuture ? 'No hay datos para meses futuros' : 'No hay gastos añadidos'}</p>
            ) : (
              visibleExpenses.map(expense => {
                // variable con frecuencia programada y sin importe → pendiente
                const isPendingVariable = expenseView !== 'real' && isExpensePendingVariable(expense, currentYear, currentMonth);
                const monthly = isPendingVariable ? 0 : getAmountForView(expense, expenseView);
                const isPaying = expenseView === 'real' || isPaymentMonth(expense, currentYear, currentMonth);
                const showPendingInput = isPendingVariable && isPaymentMonth(expense, currentYear, currentMonth);
                const expPct = expense.expense_percentage != null ? expense.expense_percentage : ownershipPct;
                const freqLabel = { trimestral: 'Trimestral', anual: 'Anual', unico: 'Único', manual: 'Manual', custom: `Cada ${expense.custom_frequency_months}m`, mensual: null }[expense.frequency] || null;
                const typeLabel = { recurrente_fijo: 'Fijo', recurrente_variable: 'Variable', recurrente_temporal: 'Temporal', puntual: 'Único' }[expense.type] || null;
                return (
                  <div key={expense.id} className="expense-item" style={{ opacity: expense.active === false ? 0.5 : 1 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span>{expense.name}</span>
                        {expense.attachment_url && (
                          /\.(jpg|jpeg|png|gif|webp|heic|heif|avif)(\?|$)/i.test(expense.attachment_url)
                            ? (
                              <button type="button" onClick={(e) => { e.stopPropagation(); setAttachmentLightbox(expense.attachment_url); }} title="Ver imagen" style={{ background: 'none', border: 'none', padding: 0, color: '#90CAF9', lineHeight: 1, display: 'flex', alignItems: 'center', cursor: 'zoom-in' }}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                              </button>
                            ) : (
                              <a href={expense.attachment_url} target="_blank" rel="noreferrer" title="Ver adjunto" onClick={e => e.stopPropagation()} style={{ color: '#90CAF9', lineHeight: 1, display: 'flex', alignItems: 'center' }}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                              </a>
                            )
                        )}
                        {typeLabel && <span className={`frequency-badge ${expense.type}`} style={{ fontSize: 10 }}>{typeLabel}</span>}
                        {freqLabel && expense.frequency !== 'unico' && (
                          <span
                            className={`frequency-badge ${expense.frequency}`}
                            style={!isPaying ? { opacity: 0.4 } : undefined}
                          >{freqLabel}</span>
                        )}
                        {expense.type === 'recurrente_temporal' && expense.duration_payments && (
                          <span style={{ fontSize: 10, color: '#aaa' }}>({calcPaymentsMade(expense)}/{expense.duration_payments} pagos)</span>
                        )}
                        {expPct !== ownershipPct && (
                          <span style={{ fontSize: 10, color: '#888' }}>{expPct}%</span>
                        )}
                        {expense.active === false && <span style={{ fontSize: 10, color: '#aaa' }}>Pausado</span>}
                      </div>
                      {showPendingInput && (
                        <PendingVariableInput expenseId={expense.id} onSave={handleUpdateVariableAmount} />
                      )}
                    </div>
                    <div className="expense-actions" style={{ gap: 6 }}>
                      <span style={{ color: isPendingVariable ? '#FFA726' : undefined }}>
                        {isPendingVariable
                          ? '— €'
                          : expense.frequency === 'manual' && !expense.amount
                            ? '— €'
                            : `${monthly.toFixed(2)} ${expenseView === 'real' ? '€' : '€/mes'}`}
                      </span>
                      {expense.frequency === 'manual' && (
                        <PendingVariableInput expenseId={expense.id} onSave={handleUpdateVariableAmount} buttonLabel="+ Importe" />
                      )}
                      {canEdit && (
                        <ExpenseMenu
                          expenseId={expense.id}
                          openMenuId={openMenuExpenseId}
                          setOpenMenuId={setOpenMenuExpenseId}
                          onEdit={() => { setEditingExpense(expense); setOpenMenuExpenseId(null); }}
                          onDelete={() => handleDeleteExpense(expense)}
                        />
                      )}
                    </div>
                  </div>
                );
              })
            )}
            {visibleExpenses.length > 0 && (
              <div className="expense-item total"><strong>{expenseView === 'real' ? 'Total mes' : 'Equiv. mensual'}</strong><strong>{totalExpenses.toFixed(2)} €</strong></div>
            )}
            {canEdit && <button className="add-expense-button" onClick={() => setShowAddExpense(true)}>+ Añadir gasto</button>}
          </div>
        )}
        {!showExpenses && canEdit && (
          <button className="add-expense-button" onClick={() => setShowAddExpense(true)}>+ Añadir gasto</button>
        )}
      </div>

      {/* Historial */}
      <MonthlyHistoryCard
        name={property.name}
        exportTitle="Historial vacacional"
        startYear={historyStart.year}
        startMonth={historyStart.month}
        computeMonth={computeHistoryMonth}
        onSelectMonth={(y, m) => { setCurrentYear(y); setCurrentMonth(m); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
      />

      {/* Datos de inversión */}
      <div className="info-card">
        <div className="card-header clickable" onClick={() => setShowInvestmentSection(!showInvestmentSection)}>
          <h3>Datos de inversión</h3>
          <span className="arrow">{showInvestmentSection ? '▼' : '›'}</span>
        </div>

        {showInvestmentSection && (
          <div style={{ padding: '4px 4px 8px' }}>
            <InvestmentForm
              property={property}
              expenses={expenses}
              onUpdate={onUpdate}
              landlordEmail={landlordEmail}
              onExpenseAdded={refreshExpenses}
              monthlyIncomeOverride={vacMonthlyIncome}
              avgIncomeOverride={vacAvgRecentIncome}
            />
          </div>
        )}
      </div>

      {/* Modals */}
      {showAddBooking && (
        <BookingModal
          booking={editingBooking}
          onClose={() => { setShowAddBooking(false); setEditingBooking(null); }}
          onSave={handleAddBooking}
          onDelete={editingBooking ? () => { handleDeleteBooking(editingBooking.id); setShowAddBooking(false); setEditingBooking(null); } : null}
        />
      )}
      {(showAddExpense || editingExpense) && (
        <AddExpenseModal
          onClose={() => { setShowAddExpense(false); setEditingExpense(null); }}
          onAdd={handleAddExpense}
          onUpdate={handleUpdateExpense}
          defaultExpensePct={property.ownershipPercentage || 100}
          initialExpense={editingExpense}
          landlordEmail={landlordEmail}
          propertyId={property.id}
          defaultDate={`${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-01`}
          isPastMonth={isPastMonth}
          existingExpenses={expenses}
        />
      )}
      {deleteExpenseModal && (
        <DeleteExpenseModal
          expense={deleteExpenseModal.expense}
          onClose={() => setDeleteExpenseModal(null)}
          onSkipPeriod={handleSkipExpenseMonth}
          onDeletePermanently={handleDeleteExpensePermanently}
        />
      )}
      {duplicateExpenseModal && (
        <DuplicateExpenseModal
          data={duplicateExpenseModal}
          onClose={() => setDuplicateExpenseModal(null)}
          onExtend={handleExtendExistingExpense}
          onAddAnyway={async () => { const { newExpenseData, prevStartDates } = duplicateExpenseModal; setDuplicateExpenseModal(null); await doInsertExpense(newExpenseData, prevStartDates); }}
        />
      )}
      {/* Image lightbox for expense attachments */}
      {attachmentLightbox && (
        <div
          onClick={() => setAttachmentLightbox(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <button
            onClick={() => setAttachmentLightbox(null)}
            aria-label="Cerrar"
            style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', color: '#fff', fontSize: 36, cursor: 'pointer', lineHeight: 1, padding: '4px 10px' }}
          >×</button>
          <img
            src={attachmentLightbox}
            alt="Adjunto"
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: '95vw', maxHeight: '90dvh', objectFit: 'contain', borderRadius: 8 }}
          />
        </div>
      )}
      {showEditProperty && (
        <EditVacationalModal
          property={property}
          onClose={() => setShowEditProperty(false)}
          onSave={(data) => { onUpdate({ ...property, ...data }); setShowEditProperty(false); }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Modal añadir/editar reserva
// ─────────────────────────────────────────────
function BookingModal({ booking, onClose, onSave, onDelete }) {
  const [guestName, setGuestName] = useState(booking?.guestName || '');
  const [guestPhone, setGuestPhone] = useState(booking?.guestPhone || '');
  const [startDate, setStartDate] = useState(booking?.startDate || '');
  const [endDate, setEndDate] = useState(booking?.endDate || '');
  const [amount, setAmount] = useState(booking?.amount?.toString() || '');
  const [platform, setPlatform] = useState(booking?.platform || 'directo');
  const [status, setStatus] = useState(booking?.status || 'confirmed');
  const [notes, setNotes] = useState(booking?.notes || '');

  const nights = startDate && endDate
    ? Math.round((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24))
    : 0;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ guestName, guestPhone, startDate, endDate, amount: parseFloat(amount), platform, status, notes });
  };

  const platforms = [
    { key: 'airbnb', label: 'Airbnb', color: '#FF5A5F' },
    { key: 'booking', label: 'Booking', color: '#003580' },
    { key: 'directo', label: 'Directo', color: '#4CAF50' },
    { key: 'otro', label: 'Otro', color: '#9C27B0' },
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{booking ? 'Editar reserva' : 'Nueva reserva'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Nombre del huésped</label>
            <input type="text" placeholder="Ej: Carlos García" value={guestName} onChange={e => setGuestName(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>Teléfono (opcional)</label>
            <input type="tel" placeholder="Ej: 612 345 678" value={guestPhone} onChange={e => setGuestPhone(e.target.value)} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div className="form-group">
              <label>Fecha entrada</label>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} required />
            </div>
            <div className="form-group">
              <label>Fecha salida</label>
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} required />
            </div>
          </div>
          {nights > 0 && (
            <p style={{ fontSize: '12px', color: '#888', margin: '-8px 0 12px', textAlign: 'center' }}>
              {nights} noche{nights !== 1 ? 's' : ''}
            </p>
          )}
          <div className="form-group">
            <label>Importe total (€)</label>
            <input type="number" placeholder="0" min="0" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>Plataforma</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
              {platforms.map(p => (
                <button key={p.key} type="button" onClick={() => setPlatform(p.key)}
                  style={{
                    padding: '10px 6px', border: `2px solid ${platform === p.key ? p.color : '#ddd'}`,
                    borderRadius: '10px', background: platform === p.key ? p.color + '15' : 'white',
                    cursor: 'pointer', fontSize: '13px', fontWeight: platform === p.key ? 700 : 400,
                    color: platform === p.key ? p.color : '#555'
                  }}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div className="form-group">
            <label>Estado</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
              {[['confirmed','Confirmada','#4CAF50'], ['pending','Pendiente','#FF9800'], ['cancelled','Cancelada','#F44336']].map(([key, label, color]) => (
                <button key={key} type="button" onClick={() => setStatus(key)}
                  style={{
                    padding: '10px', border: `2px solid ${status === key ? color : '#ddd'}`,
                    borderRadius: '10px', background: status === key ? color + '15' : 'white',
                    cursor: 'pointer', fontSize: '13px', fontWeight: status === key ? 700 : 400,
                    color: status === key ? color : '#555'
                  }}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="form-group">
            <label>Notas (opcional)</label>
            <input type="text" placeholder="Ej: late check-in, mascotas..." value={notes} onChange={e => setNotes(e.target.value)} />
          </div>
          <div className="modal-buttons">
            <button type="submit" className="submit-button">{booking ? 'Guardar cambios' : 'Añadir reserva'}</button>
            {onDelete && <button type="button" className="delete-button" onClick={onDelete}>Eliminar reserva</button>}
          </div>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Modal editar propiedad vacacional
// ─────────────────────────────────────────────
function EditVacationalModal({ property, onClose, onSave }) {
  const [name, setName] = useState(property.name);
  const [price, setPrice] = useState(property.price?.toString() || '');
  const [ownershipPercentage, setOwnershipPercentage] = useState(property.ownershipPercentage?.toString() || '100');

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Editar propiedad</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={e => { e.preventDefault(); onSave({ name, price: parseFloat(price), ownershipPercentage: parseFloat(ownershipPercentage) }); }}>
          <div className="form-group">
            <label>Nombre/Dirección</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>Ingresos de referencia (€/mes)</label>
            <input type="number" placeholder="1000" value={price} onChange={e => setPrice(e.target.value)} required />
            <p className="payment-range-note" style={{ marginTop: '8px' }}>Usado como referencia para el gauge de rentabilidad</p>
          </div>
          <div className="form-group">
            <label>Mi porcentaje de propiedad (%)</label>
            <input type="number" min="1" max="100" value={ownershipPercentage} onChange={e => setOwnershipPercentage(e.target.value)} required />
          </div>
          <button type="submit" className="submit-button">Guardar cambios</button>
        </form>
      </div>
    </div>
  );
}

export default VacationalDetail;